import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Truck, UploadCloud } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import BulkUploadModal from '../../components/ui/BulkUploadModal';
import { useAuth } from '../../context/AuthContext';

const emptyForm = { name: '', contactPerson: '', phone: '', email: '', address: '' };
const SUPPLIER_TEMPLATE_HEADERS = ['Name', 'Contact Person', 'Phone', 'Email', 'Address'];
const SUPPLIER_TEMPLATE_REQUIRED = ['Name'];
const SUPPLIER_TEMPLATE_SAMPLE = ['Lily Worldwide Private Limited', 'Rahul Mehta', '9876543210', 'ops@lily.com', 'Plot 14, GIDC, Vadodara'];

const SupplierList = () => {
  const { can } = useAuth();
  const canManage = can('suppliers.manage');

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchSuppliers = useCallback(() => {
    setLoading(true);
    api.get('/suppliers')
      .then((r) => setSuppliers(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const openCreate = () => { setEditTarget(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (s) => {
    setEditTarget(s);
    setForm({
      name: s.name, contactPerson: s.contactPerson || '',
      phone: s.phone || '', email: s.email || '', address: s.address || '',
    });
    setShowForm(true);
  };

  const handleDeactivate = async (s) => {
    try {
      await api.delete(`/suppliers/${s._id}`);
      toast.success('Supplier deactivated');
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate supplier');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editTarget) {
        await api.put(`/suppliers/${editTarget._id}`, form);
        toast.success('Supplier updated');
      } else {
        await api.post('/suppliers', form);
        toast.success('Supplier created');
      }
      setShowForm(false);
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save supplier');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      label: 'Supplier', key: 'name',
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-zip-red/10 border border-zip-red/20 flex items-center justify-center flex-shrink-0">
            <Truck size={14} className="text-zip-red" />
          </div>
          <span className="font-medium text-zip-text">{r.name}</span>
        </div>
      ),
    },
    { label: 'Contact', key: 'contactPerson', render: (r) => <span className="text-zip-muted text-sm">{r.contactPerson || '—'}</span> },
    { label: 'Phone', key: 'phone', render: (r) => <span className="font-mono text-zip-muted text-xs">{r.phone || '—'}</span> },
    { label: 'Email', key: 'email', render: (r) => <span className="font-mono text-zip-muted text-xs">{r.email || '—'}</span> },
    { label: 'Address', key: 'address', render: (r) => <span className="text-zip-muted text-xs">{r.address || '—'}</span> },
    ...(canManage ? [{
      label: '', key: 'actions',
      render: (r) => (
        <div className="flex items-center gap-3">
          <button onClick={() => openEdit(r)} className="text-xs font-mono text-zip-muted hover:text-zip-text transition-colors">Edit</button>
          <button onClick={() => handleDeactivate(r)} className="text-xs font-mono text-zip-muted hover:text-zip-red transition-colors">Deactivate</button>
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zip-muted">Vendors that goods are received from.</p>
        {canManage && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowBulkUpload(true)} className="btn-ghost flex items-center gap-2 whitespace-nowrap">
              <UploadCloud size={15} /> Bulk Upload
            </button>
            <button onClick={openCreate} className="btn-primary flex items-center gap-2">
              <Plus size={15} /> Add Supplier
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-12 skeleton rounded" />)}
        </div>
      ) : (
        <DataTable columns={columns} rows={suppliers} emptyText="No suppliers yet. Click Add Supplier to create one." />
      )}

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editTarget ? 'Edit Supplier' : 'Add Supplier'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Supplier Name *</label>
            <input className="zip-input" required placeholder="Global Packaging Co."
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Contact Person</label>
              <input className="zip-input" placeholder="Sunil Shah"
                value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Phone</label>
              <input className="zip-input font-mono" placeholder="9812345678"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Email</label>
            <input type="email" className="zip-input" placeholder="sales@globalpack.com"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Address</label>
            <textarea className="zip-input resize-none" rows={2} placeholder="Street, city, state, pincode"
              value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving…</>
                : editTarget ? 'Save Changes' : 'Create Supplier'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </Modal>

      <BulkUploadModal
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        title="Bulk Upload Suppliers"
        endpoint="/suppliers/bulk-upload"
        templateHeaders={SUPPLIER_TEMPLATE_HEADERS}
        templateSample={SUPPLIER_TEMPLATE_SAMPLE}
        requiredColumns={SUPPLIER_TEMPLATE_REQUIRED}
        templateFilename="suppliers_template.csv"
        onDone={fetchSuppliers}
      />
    </div>
  );
};

export default SupplierList;
